import { bridge } from '../native/bridge';
import { decodePrincipal, type Principal } from './principal';
import { tokenStore } from './tokenStore';

/**
 * The platform's web single sign-on. `invite.backend` sets the JWT as a readable `Authorization`
 * cookie on the registrable domain on every login (`formatCookie` in authentication.service.ts),
 * and the admin client reads it the same way (`getCookie('Authorization')`). Any first-party Invite
 * app on that domain can therefore adopt an existing session instead of asking for the password
 * again. Only used in a browser: inside the native shell the token store is the authority.
 */
const COOKIE_NAME = 'Authorization';

const readCookie = (name: string): string | null => {
  let jar: string;
  try {
    jar = document.cookie;
  } catch {
    return null;
  }
  for (const part of jar.split(';')) {
    const entry = part.trim();
    // Split on the first '=' only. A JWT is base64url and carries none, but the admin client's
    // reader assumes that; ours does not have to.
    const separator = entry.indexOf('=');
    if (separator > 0 && entry.slice(0, separator) === name) {
      return decodeURIComponent(entry.slice(separator + 1));
    }
  }
  return null;
};

/**
 * Every domain the cookie could be scoped to, narrowest first: the host itself, then each parent.
 * A single-label host such as `localhost` has no parents, which is what the backend uses in
 * development (`getTopLevelDomain`).
 */
const cookieDomains = (hostname: string): string[] => {
  const labels = hostname.split('.');
  if (labels.length < 2) {
    return [hostname];
  }
  const domains: string[] = [];
  for (let i = 0; i <= labels.length - 2; i += 1) {
    domains.push(labels.slice(i).join('.'));
  }
  return domains;
};

export interface SharedSession {
  token: string;
  expiresAt: Date;
}

/**
 * The shared session, or null when there is none this app may use: no cookie, an undecodable or
 * expiry-less token, an expired one, or a principal the app does not `accept` (a guest session
 * must never open the Operations app, so Operations passes its scope check).
 */
export const readSharedSession = (accept: (p: Principal) => boolean): SharedSession | null => {
  const token = readCookie(COOKIE_NAME);
  if (token === null || token === '') {
    return null;
  }
  let principal;
  try {
    principal = decodePrincipal(token);
  } catch {
    return null;
  }
  if (principal.exp === undefined) {
    return null;
  }
  const expiresAt = new Date(principal.exp * 1000);
  if (expiresAt.getTime() <= Date.now()) {
    return null;
  }
  if (!accept(principal)) {
    return null;
  }
  return { token, expiresAt };
};

/**
 * Signing out has to remove the shared cookie as well, or the next load signs the user straight
 * back in. `POST /auth/logout` answers with `Authorization=;Max-age=0` and neither `Path` nor
 * `domain`, so it does not match the cookie the login set; expire it here on every domain it
 * could be scoped to.
 */
export const clearSharedSession = (): void => {
  const base = `${COOKIE_NAME}=; Max-Age=0; Path=/`;
  try {
    document.cookie = base;
    for (const domain of cookieDomains(window.location.hostname)) {
      document.cookie = `${base}; domain=${domain}`;
      document.cookie = `${base}; domain=.${domain}`;
    }
  } catch {
    // Cookies unavailable: nothing to clear.
  }
};

/**
 * App-start step: in a browser with no session of its own, adopt the platform's shared one. Runs
 * once from `main.tsx`, before the first render, so a signed-in user never sees the login form.
 * Inside the native shell this does nothing; there the shell's token store is the authority and
 * `AuthProvider` consults it asynchronously instead.
 */
export const adoptSharedSession = (accept: (p: Principal) => boolean): boolean => {
  if (tokenStore.getToken() !== null || bridge.isAvailable()) {
    return false;
  }
  const shared = readSharedSession(accept);
  if (shared === null) {
    return false;
  }
  tokenStore.setToken(shared.token, shared.expiresAt);
  return true;
};
